import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { TextField, IconButton } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NodeOutputPlaceholderChipInput from './NodeOutputPlaceholderChipInput';
import NodeSelect from './NodeSelect';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePlaceholderOutputRow — Output placeholder row: description + key + type selector + delete.
 * Used by: AI Assistant, AI Agent, Code.
 */

const Root = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  width: '100%',
});

const Fields = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 0,
});

const Row = styled('div')({
  display: 'flex',
  gap: 6,
  alignItems: 'center',
});

const KeyField = styled('div')({
  flex: 1,
  minWidth: 0,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  marginTop: '2px',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  '&:hover': { color: theme.palette.error.main },
}));

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'datetime', label: 'DateTime' },
  { value: 'json', label: 'JSON' },
  { value: 'list', label: 'List' },
  { value: 'html', label: 'HTML' },
];

const NodePlaceholderOutputRow = React.memo(function NodePlaceholderOutputRow({
  description = '',
  placeholderKey = '',
  type = 'text',
  onDescriptionChange,
  onKeyChange,
  onTypeChange,
  onDelete,
  typeOptions = TYPE_OPTIONS,
  sx,
}) {
  return (
    <Root onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation} sx={sx}>
      <Fields>
        <StyledTextField
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Description"
          value={description}
          onChange={(e) => onDescriptionChange?.(e.target.value)}
        />
        <Row>
          <KeyField>
            <NodeOutputPlaceholderChipInput
              value={placeholderKey}
              type={type}
              onChange={onKeyChange}
              placeholder="Key"
            />
          </KeyField>
          <NodeSelect
            value={type}
            onChange={onTypeChange}
            options={typeOptions}
            sx={{ maxWidth: 100 }}
          />
        </Row>
      </Fields>
      {onDelete && (
        <DeleteButton size="small" onClick={(e) => { stopPropagation(e); onDelete(); }}>
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </DeleteButton>
      )}
    </Root>
  );
});

export default NodePlaceholderOutputRow;
